import { useEffect, useState } from "react";
import { customEvent, MissParlorEventTriggerAndTargetType } from "goi_common";

export interface RegionTab {
  id: string;
  label: string;
  /** 탭에 함께 노출하는 개수. 없으면 라벨만 */
  count?: number;
  trigger: MissParlorEventTriggerAndTargetType["prlr_region"];
}

interface RegionSectionTabsProps {
  tabs: RegionTab[];
}

/**
 * 섹션 이동 탭. 스크롤 위치에 따라 활성 탭이 따라온다.
 *
 * 밑줄로 활성 상태를 보여주는 형태라, 스크롤과 무관하게 첫 탭을 활성으로 고정하면
 * 실제 보고 있는 위치와 어긋난다. IntersectionObserver로 현재 섹션을 추적한다.
 */
export const RegionSectionTabs = ({ tabs }: RegionSectionTabsProps) => {
  const [activeId, setActiveId] = useState(tabs[0]?.id);

  useEffect(() => {
    const sections = tabs
      .map((tab) => document.getElementById(tab.id))
      .filter((el): el is HTMLElement => el !== null);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // 화면에 걸친 섹션 중 가장 위에 있는 것을 활성으로 본다
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible[0]) setActiveId(visible[0].target.id);
      },
      // 헤더(60px) + 탭(48px)에 가려진 섹션이 활성으로 잡히지 않게 위를 빼둔다
      { rootMargin: "-116px 0px -60% 0px", threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [tabs]);

  return (
    <nav className="rg-tabs" aria-label="페이지 내 이동">
      <div className="rg-tabs__inner">
        {tabs.map((tab) => (
          <a
            key={tab.id}
            href={`#${tab.id}`}
            className={`rg-tab${activeId === tab.id ? " rg-tab--on" : ""}`}
            aria-current={activeId === tab.id ? "true" : undefined}
            onClick={() =>
              customEvent({
                newGtm: {
                  path: "prlr_region",
                  trigger_and_target: tab.trigger,
                },
              })
            }
          >
            <span className="rg-tab__label">{tab.label}</span>
            {tab.count !== undefined && (
              <span className="rg-tab__count">
                {tab.count.toLocaleString("ko-KR")}
              </span>
            )}
          </a>
        ))}
      </div>
    </nav>
  );
};
